import type React from 'react';

import { Button } from '@change/ds-core-ui/Button';
import { cn } from '@change/ds-utils';

import { TrackingContext } from '@change-corgi/core-react/tracking';

import { ShareSlideUp } from '../../../PromoteOrShareActions/components/ShareSlideUp';
import type { ButtonVariant } from '../../../PromoteOrShareActions/components/ShareSlideUp';
import { usePromoteOrShareActions } from '../../../PromoteOrShareActions/hooks/usePromoteOrShareActions';

type ShareButtonProps = {
	variant: ButtonVariant;
	label: string;
	trailingIcon?: React.ReactNode;
};

const TRACKING_PROPERTIES = { button_clicked: 'share_first_card' } as const;

const ShareButtonContent: React.FC<ShareButtonProps> = ({ variant, label, trailingIcon }) => {
	const {
		data: { isShareSlideUpEnabled, shareButtonUrl },
		actions: { handleShareClick },
	} = usePromoteOrShareActions();

	return (
		<>
			{isShareSlideUpEnabled && (
				/* The share slide-up is only visible on mobile. */
				<ShareSlideUp shareButtonLabel={label} buttonVariant={variant} />
			)}
			{/* The share button is hidden on mobile to leverage the slide-up. */}
			<Button
				as="a"
				variant={variant}
				size="large"
				href={shareButtonUrl}
				onClick={handleShareClick}
				className={cn('w-full text-center', { 'hidden md:inline-flex': isShareSlideUpEnabled })}
				data-qa="share-button"
				data-testid="share-button"
				trailingIcon={trailingIcon}
			>
				{label}
			</Button>
		</>
	);
};

/**
 * Card-local share button: same share URL and combo_psf_share tracking as the page's share action
 * (reused via usePromoteOrShareActions), and the same mobile ShareSlideUp behaviour
 * (the shared component itself, not a copy). The shared ShareSlideUp trigger doesn't take a
 * trailing icon, so on mobile the slide-up button renders without one -- the desktop link button
 * is the only one that shows it.
 *
 * The tracking context adds `button_clicked` to every event fired below it (including the slide-up's),
 * which tells this card's combo_psf_share apart from the page's own share action firing the same event.
 * The hook lives in the inner component so it reads the wrapped tracker.
 */
export const ShareButton: React.FC<ShareButtonProps> = (props) => (
	<TrackingContext properties={TRACKING_PROPERTIES}>
		<ShareButtonContent {...props} />
	</TrackingContext>
);
